
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const AboutPage: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY) {
        // Scrolling up
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down
        setIsHeaderVisible(false);
        setIsMobileMenuOpen(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 bg-white shadow-sm z-50 transition-transform duration-300 ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <span className="text-2xl font-bold text-black select-none cursor-pointer" style={{ fontFamily: '"Pacifico", serif' }}>
                Novatronis
              </span>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="hover:text-blue-400 transition-colors cursor-pointer">Home</Link>
              <Link to="/services" className="hover:text-blue-400 transition-colors cursor-pointer">Services</Link>
              <Link to="/solutions" className="hover:text-blue-400 transition-colors cursor-pointer">Solutions</Link>
              <Link to="/about" className="text-blue-400 cursor-pointer">About</Link>
              <Link to="/contact" className="hover:text-blue-400 transition-colors cursor-pointer">Contact</Link>
            </div>
            <div className="md:hidden">
              <button 
                className="text-gray-600 cursor-pointer"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>
          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 mt-4 py-4">
              <div className="flex flex-col space-y-4">
                <Link 
                  to="/" 
                  className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                  onClick={handleMobileNavClick}
                >
                  Home
                </Link>
                <Link 
                  to="/services" 
                  className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                  onClick={handleMobileNavClick}
                >
                  Services
                </Link>
                <Link 
                  to="/solutions" 
                  className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                  onClick={handleMobileNavClick}
                >
                  Solutions
                </Link>
                <Link 
                  to="/about" 
                  className="text-blue-400 px-6 py-2 cursor-pointer"
                  onClick={handleMobileNavClick}
                >
                  About
                </Link>
                <Link 
                  to="/contact" 
                  className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                  onClick={handleMobileNavClick}
                >
                  Contact
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-black text-white pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/b7b92a66af75f0d05955bd380936388c.jpg')`
          }}
        ></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="block">About</span>
            <span className="block text-blue-400">Novatronis</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-gray-300">
            Pioneering the future of warehouse automation with innovative solutions and unmatched expertise
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Mission & Vision
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-blue-50 p-8 rounded-xl">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-target-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-lg text-gray-600">
                To empower businesses with intelligent warehouse automation solutions that drive efficiency, reduce costs, and enable sustainable growth. We are committed to delivering innovative technologies that transform operations and create lasting value for our clients.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-6">
                <i className="ri-eye-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-lg text-gray-600">
                To be the global leader in warehouse automation, setting the standard for innovation, reliability, and customer success. We envision a future where every warehouse operates with maximum efficiency through intelligent automation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              The principles that guide everything we do and define who we are as a company
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-lightbulb-line',
                title: 'Innovation',
                description: 'Continuously pushing the boundaries of technology to create breakthrough solutions'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Reliability',
                description: 'Delivering consistent, dependable solutions that our clients can trust'
              },
              {
                icon: 'ri-team-line',
                title: 'Partnership',
                description: 'Building long-term relationships based on mutual success and collaboration'
              },
              {
                icon: 'ri-award-line',
                title: 'Excellence',
                description: 'Maintaining the highest standards in everything we do, from design to support'
              }
            ].map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold mb-4 text-blue-400">{value.title}</h3>
                <p className="text-gray-300">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Bottom Navigation */}
      {/* Removed mobile bottom navigation bar */}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-2xl font-bold mb-4 select-none" style={{ fontFamily: '"Pacifico", serif' }}>
                Novatronis
              </h3>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Transforming warehouse operations with cutting-edge robotics, AI-powered systems, and intelligent automation solutions. We empower businesses to achieve unprecedented levels of efficiency, accuracy, and cost savings through innovative technology.
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">
              © 2024 Novatronis Solutions. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-blue-400 transition-colors cursor-pointer">Website Builder</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
